import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import {
  ListView,
  ListViewItem,
  Text,
  ActionButtonGroup,
  ActionButton,
} from "@react-spectrum/s2";
import PlayIcon from "@react-spectrum/s2/icons/Play";
import PauseIcon from "@react-spectrum/s2/icons/Pause";
import StarIcon from "@react-spectrum/s2/icons/Star";
import StarFilledIcon from "@react-spectrum/s2/icons/StarFilled";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const getGenderText = (gender) => {
  switch (gender.toLowerCase()) {
    case "f":
      return "Female";
    case "m":
      return "Male";
    case "nb":
      return "Non-binary";
    default:
      return "";
  }
};

export const VoicesList = ({
  voices = [],

  defaultVoiceId = null,
  voiceId = null,
  onVoiceSelect,

  ...otherProps
}) => {
  const audioRef = useRef(new Audio());
  const [selectedVoiceId, setSelectedVoiceId] = useState(defaultVoiceId);
  const [currentSampleVoiceId, setCurrentSampleVoiceId] = useState(null);
  const [isPaused, setIsPaused] = useState(true);
  const [favorites, setFavorites] = useState(
    localStorage.getItem("BeStory__favoriteVoices")?.split(",") || [],
  );

  const selectedVoice = useMemo(
    () =>
      voices?.find((voice) => voice.voiceId === (voiceId || selectedVoiceId)),
    [selectedVoiceId, voiceId, voices],
  );

  const voiceItems = useMemo(() => {
    const sortedVoices = [...voices].sort((a, b) => {
      const aFav = favorites.includes(a.voiceId);
      const bFav = favorites.includes(b.voiceId);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return 0;
    });
    return sortedVoices.map((voice) => ({
      ...voice,
      isFavorite: favorites.includes(voice.voiceId),
    }));
  }, [voices, favorites]);

  const handleVoiceSelect = useCallback(
    (key) => {
      setSelectedVoiceId(key);
      onVoiceSelect?.(key);
    },
    [onVoiceSelect],
  );

  const handleToggleFavorite = useCallback(
    (key) => {
      const newFavorites = favorites.includes(key)
        ? favorites.filter((id) => id !== key)
        : [...favorites, key];
      setFavorites(newFavorites);
      localStorage.setItem("BeStory__favoriteVoices", newFavorites.join(","));
    },
    [favorites],
  );

  const handlePlaySample = useCallback((voice) => {
    if (audioRef.current) {
      // audioRef.current.addEventListener("canplaythrough", () => {
      //   audioRef.current.play();
      // }, { once: true });
      audioRef.current.src = voice.sampleURL;
      setCurrentSampleVoiceId(voice.voiceId);
    }
  }, []);

  const handlePauseSample = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      setCurrentSampleVoiceId(null);
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    const handleEnded = () => setIsPaused(true);
    const handlePlay = () => setIsPaused(false);
    const handlePause = () => setIsPaused(true);
    const handleCanPlayThrough = () => {
      audio.play();
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, []);

  return (
    <ListView
      label="Voices"
      aria-label="Voices"
      items={voiceItems}
      dependencies={[currentSampleVoiceId, isPaused]}
      selectionMode="single"
      selectionStyle="highlight"
      selectedKeys={
        voiceId || selectedVoiceId ? [voiceId || selectedVoiceId] : []
      }
      onSelectionChange={([id]) => handleVoiceSelect(id)}
      {...otherProps}
      styles={
        style({
          flexBasis: "[0%]",
          flexGrow: 1,
          flexShrink: 1,
        }) + (otherProps.styles || "")
      }
    >
      {(voice) => (
        <ListViewItem id={voice.voiceId} textValue={voice.displayName}>
          <Text slot="label">{voice.displayName}</Text>
          <Text slot="description">
            {`${voice.ageGroup} • ${getGenderText(voice.gender)}`}
          </Text>
          <ActionButtonGroup>
            <ActionButton
              aria-label="Toggle favorite"
              onPress={() => handleToggleFavorite(voice.voiceId)}
            >
              {voice.isFavorite ? <StarFilledIcon /> : <StarIcon />}
            </ActionButton>
            {currentSampleVoiceId === voice.voiceId && !isPaused ? (
              <ActionButton
                aria-label="Pause preview"
                onPress={handlePauseSample}
              >
                <PauseIcon />
              </ActionButton>
            ) : (
              <ActionButton
                aria-label="Play preview"
                onPress={() => handlePlaySample(voice)}
              >
                <PlayIcon />
              </ActionButton>
            )}
          </ActionButtonGroup>
        </ListViewItem>
      )}
    </ListView>
  );
};
